import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// src/hooks.ts
function useObservable(observable, initialValue) {
  const [value, setValue] = useState(() => {
    return observable.getValue?.() ?? initialValue;
  });
  useEffect(() => {
    const unsub = observable.subscribe((v) => setValue(v));
    return unsub;
  }, [observable]);
  return value;
}
function useQuery(collection, query) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const queryRef = useRef(query);
  const queryKey = useMemo(() => JSON.stringify(query), [query]);
  const refresh = useCallback(() => {
    setLoading(true);
    collection.find(queryRef.current).exec().then(
      (results) => {
        setData(results);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );
  }, [collection, queryKey]);
  useEffect(() => {
    queryRef.current = query;
    refresh();
    const unsub = collection.changes$.subscribe(() => {
      collection.find(queryRef.current).exec().then(
        (results) => {
          setData(results);
          setError(null);
        },
        (err) => setError(err)
      );
    });
    return unsub;
  }, [collection, queryKey]);
  return { data, loading, error, refresh };
}
function useDoc(collection, key) {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useCallback(() => {
    if (!key) {
      setData(void 0);
      setLoading(false);
      return;
    }
    setLoading(true);
    collection.findOne(key).then(
      (doc) => {
        setData(doc);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );
  }, [collection, key]);
  useEffect(() => {
    refresh();
    const unsub = collection.changes$.subscribe(() => {
      if (key) {
        collection.findOne(key).then(
          (doc) => {
            setData(doc);
            setError(null);
          },
          (err) => setError(err)
        );
      }
    });
    return unsub;
  }, [collection, key]);
  return { data, loading, error, refresh };
}
function useMutation(collection) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrap = useCallback((fn, callbacks) => {
    setLoading(true);
    setError(null);
    return fn().then((r) => {
      setLoading(false);
      callbacks?.onSuccess?.(r);
      return r;
    }).catch((err) => {
      setError(err);
      setLoading(false);
      callbacks?.onError?.(err);
      throw err;
    });
  }, []);
  return {
    insert: useCallback((doc, callbacks) => wrap(() => collection.insert(doc), callbacks), [collection, wrap]),
    update: useCallback((doc, callbacks) => wrap(() => collection.update(doc), callbacks), [collection, wrap]),
    upsert: useCallback((doc, callbacks) => wrap(() => collection.upsert(doc), callbacks), [collection, wrap]),
    remove: useCallback((key, callbacks) => wrap(() => collection.delete(key), callbacks), [collection, wrap]),
    loading,
    error
  };
}
function useAsyncMutation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mutate = useCallback(async (fn, callbacks) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      setLoading(false);
      callbacks?.onSuccess?.(result);
      return result;
    } catch (err) {
      const error2 = err instanceof Error ? err : new Error(String(err));
      setError(error2);
      setLoading(false);
      callbacks?.onError?.(error2);
      return null;
    }
  }, []);
  return { mutate, loading, error };
}
function useOptimisticMutation(collection) {
  const [error, setError] = useState(null);
  const mutate = useCallback(async (optimisticDoc, operation, rollback) => {
    try {
      await collection.upsert(optimisticDoc);
    } catch {
    }
    try {
      const result = await operation(optimisticDoc);
      setError(null);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      if (rollback) {
        try {
          await rollback(optimisticDoc);
        } catch {
        }
      }
      return null;
    }
  }, [collection]);
  return { mutate, error };
}
function useNetworkStatus(monitor) {
  const [status, setStatus] = useState(monitor.status);
  useEffect(() => {
    const unsub = monitor.subscribe((s) => setStatus(s));
    return unsub;
  }, [monitor]);
  return { status, isOnline: status === "online" };
}
function useSyncStatus(tracker) {
  const [status, setStatus] = useState(() => tracker.getStatus());
  useEffect(() => {
    const unsub = tracker.status$.subscribe((s) => setStatus(s));
    return unsub;
  }, [tracker]);
  return status;
}
function useAuthStatus(auth) {
  const [status, setStatus] = useState(auth.status);
  useEffect(() => {
    const unsub = auth.subscribe((s) => setStatus(s));
    return unsub;
  }, [auth]);
  return { status, isAuthenticated: status === "authenticated" || status === "refreshing" };
}
function useNotifications(manager) {
  const notifications = useObservable(manager.notifications$, []);
  const unreadCount = useObservable(manager.unreadCount$, 0);
  return {
    notifications: notifications ?? [],
    unreadCount: unreadCount ?? 0,
    add: useCallback((input) => manager.add(input), [manager]),
    markRead: useCallback((id) => manager.markRead(id), [manager]),
    markAllRead: useCallback(() => manager.markAllRead(), [manager]),
    dismiss: useCallback((id) => manager.dismiss(id), [manager]),
    dismissAll: useCallback(() => manager.dismissAll(), [manager])
  };
}
function usePushSubscription(push) {
  const permission = useObservable(push.permission$, push.permission);
  const [subscription, setSubscription] = useState(push.subscription);
  const subscribe = useCallback(async () => {
    const sub = await push.subscribe();
    setSubscription(sub);
  }, [push]);
  const unsubscribe = useCallback(async () => {
    await push.unsubscribe();
    setSubscription(null);
  }, [push]);
  return {
    permission: permission ?? "default",
    subscription,
    isSupported: push.isSupported(),
    subscribe,
    unsubscribe
  };
}
function useInfiniteQuery(fetcher, options = { pageSize: 20 }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(options.initialPage ?? 1);
  const fetchNextPage = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher(pageRef.current);
      setPages((prev) => [...prev, result.items]);
      pageRef.current++;
      const more = result.hasMore !== void 0 ? result.hasMore : result.total !== void 0 ? pages.length * options.pageSize + result.items.length < result.total : result.items.length >= options.pageSize;
      setHasMore(more);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [fetcher, loading, hasMore, pages.length, options.pageSize]);
  const refresh = useCallback(() => {
    pageRef.current = options.initialPage ?? 1;
    setPages([]);
    setHasMore(true);
    setError(null);
  }, [options.initialPage]);
  useEffect(() => {
    if (pages.length === 0 && hasMore) {
      fetchNextPage();
    }
  }, [pages.length, hasMore]);
  const data = useMemo(() => pages.flat(), [pages]);
  return {
    /** All items across all loaded pages */
    data,
    /** Array of individual pages */
    pages,
    /** Currently fetching a page */
    loading,
    /** Fetch error */
    error,
    /** Whether there are more pages to load */
    hasMore,
    /** Fetch the next page */
    fetchNextPage,
    /** Reset to page 1 and clear all data */
    refresh,
    /** Number of pages loaded */
    pageCount: pages.length
  };
}
function useCacheThroughQuery(fetcher, collection, options = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStale, setIsStale] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(0);
  const staleTime = options.staleTime ?? 3e4;
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await fetcher();
      setData(fresh);
      setIsStale(false);
      lastFetchRef.current = Date.now();
      for (const item of fresh) {
        try {
          await collection.upsert(item);
        } catch {
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [fetcher, collection]);
  useEffect(() => {
    let cancelled = false;
    collection.find(options.selector ? { selector: options.selector } : void 0).exec().then(
      (cached) => {
        if (cancelled) return;
        if (cached.length > 0) {
          setData(cached);
          setLoading(false);
          setIsStale(true);
        }
      },
      () => {
      }
      // Cache read failed — server fetch will handle it
    );
    const age = Date.now() - lastFetchRef.current;
    if (age >= staleTime) {
      refresh();
    }
    return () => {
      cancelled = true;
    };
  }, [options.selector ? JSON.stringify(options.selector) : "", staleTime]);
  return { data, loading, isStale, error, refresh };
}

export { useAsyncMutation, useAuthStatus, useCacheThroughQuery, useDoc, useInfiniteQuery, useMutation, useNetworkStatus, useNotifications, useObservable, useOptimisticMutation, usePushSubscription, useQuery, useSyncStatus };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map